import type { ReactNode } from 'react';
import { ResponseFeedback } from '@/components/form-kit';
import { BuildingSystemIcon } from '@/components/icons';
import Stepper, { type StepDef } from './Stepper';
import WorkingMessage from './WorkingMessage';
import { UserMessageBubble } from './UserMessageBubble';
import type { WorkflowProgressStep, WorkflowUiPhase, WorkflowUserMessage } from './types';

function splitThreadedUserMessages(userMessages: WorkflowUserMessage[]) {
  const initial: WorkflowUserMessage[] = [];
  const approve: WorkflowUserMessage[] = [];
  const postSuccess: WorkflowUserMessage[] = [];

  for (const message of userMessages) {
    if (message.id.startsWith('approve-')) approve.push(message);
    else if (message.id.startsWith('action-')) postSuccess.push(message);
    else initial.push(message);
  }

  return { initial, approve, postSuccess };
}

function UserMessageList({
  messages,
  onPinMessage,
  isMessagePinned,
}: {
  messages: WorkflowUserMessage[];
  onPinMessage?: (content: string) => void;
  isMessagePinned?: (content: string) => boolean;
}) {
  if (messages.length === 0) return null;

  return (
    <ul className="flow-thread__messages" aria-label="Your messages">
      {messages.map((m, i) => (
        <li
          key={m.id}
          className="flow-thread__message-wrap flow-thread__reveal"
          style={{ animationDelay: `${0.08 + i * 0.1}s` }}
        >
          <UserMessageBubble
            content={m.content}
            attachment={m.attachment}
            isPinned={isMessagePinned?.(m.content) ?? false}
            onPin={onPinMessage}
          />
        </li>
      ))}
    </ul>
  );
}

function AssistantTurn({
  children,
  className,
  showFeedback = false,
}: {
  children: ReactNode;
  className?: string;
  showFeedback?: boolean;
}) {
  return (
    <div
      className={`flow-thread__assistant flow-thread__reveal flow-thread__reveal--delay-1${className ? ` ${className}` : ''}`}
    >
      {children}
      {showFeedback ? <ResponseFeedback /> : null}
    </div>
  );
}

/** Explainability toggle and assistant system output as separate sections (Figma). */
function AssistantIntroSections({
  explainability,
  assistantHint,
  introArtifact,
  introFooter,
  trailing,
}: {
  explainability?: ReactNode;
  assistantHint?: string;
  introArtifact?: ReactNode;
  introFooter?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <>
      {explainability ? (
        <section className="flow-thread__explainability" aria-label="How I figured this out">
          {explainability}
        </section>
      ) : null}
      {assistantHint ? (
        <section className="flow-thread__assistant-output" aria-label="Assistant response">
          <p className="flow-thread__assistant-hint">{assistantHint}</p>
        </section>
      ) : null}
      {introArtifact}
      {introFooter}
      {trailing}
    </>
  );
}

export interface GuidedWorkflowFormProps<StepId extends string> {
  title: string;
  /** Site / system label in the form card header (right side). */
  systemLabel?: string;
  steps: ReadonlyArray<StepDef<StepId>>;
  activeStep: StepId;
  onStepClick: (step: StepId) => void;
  hideStepper?: boolean;
  compactFormCard?: boolean;
  persistDuringSaving?: boolean;
  /** Rendered above the current step content (e.g., AI banner). */
  headerExtra?: ReactNode;
  /** The step body for the current step. */
  content: ReactNode;
}

export interface GuidedWorkflowSuccessIntro {
  explainability?: ReactNode;
  /** Review-phase assistant message kept visible above the success block. */
  priorAssistantHint?: string;
  /** Opening assistant message shown above the success card. */
  introAssistantHint?: string;
  /** Post-completion headline above the success card. */
  successHeadline?: string;
  /** Card kept visible above the success message (e.g. bulk import artifact). */
  introArtifact?: ReactNode;
}

export interface GuidedWorkflowPostSuccessExchange {
  message: WorkflowUserMessage;
  followUp?: ReactNode;
}

export interface GuidedWorkflowPanelProps<StepId extends string> {
  uiPhase: WorkflowUiPhase;
  statusLabel: string;
  userMessages: WorkflowUserMessage[];
  progressRail: WorkflowProgressStep[];

  idleHint?: string;
  savingTitle?: string;

  form?: GuidedWorkflowFormProps<StepId>;
  explainability?: ReactNode;
  /** Shown under “How I figured this out”, above the form card. */
  assistantHint?: string;
  /** Optional card rendered below the assistant hint (e.g. bulk import artifact). */
  introArtifact?: ReactNode;
  /** Rendered below the artifact (e.g. feedback + approve). */
  introFooter?: ReactNode;
  /** Keep intro (hint, artifact, footer) visible while saving. */
  persistIntroDuringSaving?: boolean;
  /** Hide the compact saving skeleton under the phase label. */
  suppressSavingSkeleton?: boolean;
  successIntro?: GuidedWorkflowSuccessIntro;
  success?: ReactNode;
  /** User action + assistant follow-up pairs after success (e.g. credential issue). */
  postSuccessExchanges?: GuidedWorkflowPostSuccessExchange[];
  /** Rendered after the success card when not using {@link postSuccessExchanges}. */
  postSuccessContent?: ReactNode;
  /** Hide messages, intro, and form during saving — only the saving UI is shown. */
  exclusiveSavingScreen?: boolean;
  /** Pin a sent message to saved actions (slash menu). */
  onPinMessage?: (content: string) => void;
  /** Whether a message template is already pinned. */
  isMessagePinned?: (content: string) => boolean;
}

export default function GuidedWorkflowPanel<StepId extends string>({
  uiPhase,
  statusLabel,
  userMessages,
  progressRail,
  idleHint,
  savingTitle = 'Saving…',
  form,
  explainability,
  assistantHint,
  introArtifact,
  introFooter,
  persistIntroDuringSaving = false,
  suppressSavingSkeleton = false,
  successIntro,
  success,
  postSuccessExchanges,
  postSuccessContent,
  exclusiveSavingScreen = false,
  onPinMessage,
  isMessagePinned,
}: GuidedWorkflowPanelProps<StepId>) {
  const isIdle = uiPhase === 'idle';
  const isThinking = uiPhase === 'thinking';
  const isWorking = uiPhase === 'working' || uiPhase === 'almost-ready';
  const isSaving = uiPhase === 'saving';
  const savingOnly = isSaving && exclusiveSavingScreen;

  const hasApproveMessage = userMessages.some((message) => message.id.startsWith('approve-'));
  const hasThreadIntro = Boolean(explainability || assistantHint || introArtifact || introFooter);

  const persistFormDuringSaving =
    Boolean(form?.persistDuringSaving) && isSaving && !exclusiveSavingScreen;
  const hideFormAfterApprove = hasApproveMessage && uiPhase === 'form';
  const showFormSection =
    Boolean(form) && (uiPhase === 'form' || persistFormDuringSaving) && !hideFormAfterApprove;
  const showIntro =
    (uiPhase === 'form' || persistFormDuringSaving || (isSaving && persistIntroDuringSaving)) &&
    hasThreadIntro;
  const showIntroBlock =
    showIntro || (isSaving && persistIntroDuringSaving && hasThreadIntro);
  const showSavingInForm = isSaving && persistFormDuringSaving;
  const showWorkingMessage =
    isThinking || isWorking || (isSaving && !showSavingInForm && !savingOnly);
  const showSuccess = uiPhase === 'success' && Boolean(success);
  const progressAllComplete =
    uiPhase === 'form' || uiPhase === 'success' || uiPhase === 'almost-ready';

  const workingCardTitle =
    uiPhase === 'almost-ready'
      ? 'Almost ready…'
      : uiPhase === 'saving'
        ? savingTitle
        : "I'm working on your request";

  const useConversationThread =
    hasApproveMessage ||
    (uiPhase === 'form' && hasThreadIntro) ||
    (isSaving && persistIntroDuringSaving) ||
    (showSuccess && (Boolean(successIntro?.priorAssistantHint) || hasApproveMessage));

  const { initial: initialUserMessages, approve: approveUserMessages } =
    splitThreadedUserMessages(userMessages);

  const keepThreadIntroOnSuccess =
    showSuccess && (Boolean(successIntro?.priorAssistantHint) || hasApproveMessage);

  const reviewIntroBlock =
    showIntroBlock || keepThreadIntroOnSuccess ? (
      <AssistantTurn>
        {showIntroBlock ? (
          <AssistantIntroSections
            explainability={explainability}
            assistantHint={assistantHint}
            introArtifact={introArtifact}
            introFooter={introFooter}
          />
        ) : (
          <AssistantIntroSections
            explainability={successIntro?.explainability}
            assistantHint={successIntro?.priorAssistantHint}
            introArtifact={successIntro?.introArtifact}
          />
        )}
      </AssistantTurn>
    ) : null;

  const successFollowUpBlock =
    showSuccess && successIntro ? (
      <AssistantTurn className="flow-thread__assistant--success">
        <AssistantIntroSections
          explainability={successIntro.priorAssistantHint ? undefined : successIntro.explainability}
          assistantHint={successIntro.introAssistantHint}
          trailing={
            successIntro.successHeadline ? (
              <p className="flow-thread__success-headline">{successIntro.successHeadline}</p>
            ) : null
          }
        />
      </AssistantTurn>
    ) : null;

  if (savingOnly) {
    return (
      <article
        className="flow-thread-root flow-thread flow-thread--saving flow-thread--saving-only"
        data-phase="saving"
      >
        <div className="flow-thread__saving-screen flow-thread__reveal">
          <WorkingMessage
            phaseLabel={statusLabel}
            cardTitle={savingTitle}
            showProgressCard={false}
            showSkeleton
            showSavingSkeleton={false}
          />
        </div>
      </article>
    );
  }

  return (
    <article
      className={`flow-thread-root flow-thread${isIdle ? ' flow-thread--idle flow-thread-root--idle' : ''}${isSaving ? ' flow-thread--saving' : ''}${useConversationThread ? ' flow-thread--threaded' : ''}`}
      data-phase={uiPhase}
    >
      {useConversationThread ? (
        <>
          <UserMessageList
            messages={initialUserMessages}
            onPinMessage={onPinMessage}
            isMessagePinned={isMessagePinned}
          />
          {reviewIntroBlock}
          <UserMessageList
            messages={approveUserMessages}
            onPinMessage={onPinMessage}
            isMessagePinned={isMessagePinned}
          />
        </>
      ) : (
        <>
          <UserMessageList
            messages={userMessages}
            onPinMessage={onPinMessage}
            isMessagePinned={isMessagePinned}
          />
          {showIntroBlock ? (
            <AssistantTurn>
              <AssistantIntroSections
                explainability={explainability}
                assistantHint={assistantHint}
                introArtifact={introArtifact}
                introFooter={introFooter}
              />
            </AssistantTurn>
          ) : null}
        </>
      )}

      {showWorkingMessage ? (
        <div className={isSaving ? 'flow-thread__saving-banner flow-thread__reveal' : undefined}>
          <WorkingMessage
            phaseLabel={isSaving ? savingTitle : statusLabel}
            uiPhase={uiPhase}
            cardTitle={workingCardTitle}
            steps={progressRail}
            allComplete={progressAllComplete}
            showProgressCard={!isThinking && !isSaving && progressRail.length > 0}
            showSkeleton={isThinking}
            showSavingSkeleton={isSaving && !suppressSavingSkeleton}
          />
        </div>
      ) : null}

      {isIdle && idleHint ? (
        <header className="flow-thread__header flow-thread__reveal">
          <p className="flow-thread__idle-hint">{idleHint}</p>
        </header>
      ) : null}

      {showFormSection && form ? (
        <section
          key={`form-${String(form.activeStep)}`}
          className={`flow-thread__wizard flow-thread__reveal flow-thread__reveal--delay-2${form.compactFormCard ? ' flow-thread__wizard--compact' : ''}${isSaving ? ' flow-thread__wizard--locked' : ''}`}
          aria-label="Workflow form"
        >
          <div className="flow-thread__wizard-header">
            <h2 className="flow-thread__wizard-title">{form.title}</h2>
            {form.systemLabel ? (
              <span className="flow-thread__wizard-system">
                <BuildingSystemIcon size={16} className="flow-thread__wizard-system-icon" />
                <span>{form.systemLabel}</span>
              </span>
            ) : null}
          </div>

          {!form.hideStepper ? (
            <>
              <Stepper steps={form.steps} activeStep={form.activeStep} onStepClick={form.onStepClick} />
              <hr className="flow-thread__wizard-stepper-divider" aria-hidden />
            </>
          ) : null}

          {form.headerExtra ? (
            <div className="flow-thread__wizard-extra flow-thread__reveal">{form.headerExtra}</div>
          ) : null}

          {showSavingInForm ? (
            <div className="flow-thread__saving-banner flow-thread__saving-banner--in-form">
              <WorkingMessage
                phaseLabel={statusLabel}
                uiPhase={uiPhase}
                cardTitle={savingTitle}
                showProgressCard={false}
                showSkeleton={false}
                showSavingSkeleton={false}
              />
            </div>
          ) : null}

          {form.content}
        </section>
      ) : null}

      {useConversationThread ? successFollowUpBlock : null}

      {!useConversationThread && showSuccess && successIntro ? (
        <AssistantTurn className="flow-thread__assistant--success-intro">
          <AssistantIntroSections
            explainability={successIntro.explainability}
            assistantHint={successIntro.priorAssistantHint ?? successIntro.introAssistantHint}
            introArtifact={successIntro.introArtifact}
            trailing={
              successIntro.successHeadline ? (
                <p className="flow-thread__success-headline">{successIntro.successHeadline}</p>
              ) : null
            }
          />
        </AssistantTurn>
      ) : null}

      {showSuccess ? (
        <div className="flow-thread__success-block flow-thread__reveal flow-thread__reveal--delay-2">
          {success}
        </div>
      ) : null}

      {useConversationThread && postSuccessExchanges && postSuccessExchanges.length > 0
        ? postSuccessExchanges.map(({ message, followUp }) => (
            <div key={message.id} className="flow-thread__chat-exchange">
              <UserMessageList
                messages={[message]}
                onPinMessage={onPinMessage}
                isMessagePinned={isMessagePinned}
              />
              {followUp ? (
                <div className="flow-thread__chat-exchange-assistant">{followUp}</div>
              ) : null}
            </div>
          ))
        : null}

      {postSuccessContent &&
      (!useConversationThread || !postSuccessExchanges || postSuccessExchanges.length === 0) ? (
        <div className="flow-thread__post-success flow-thread__reveal">{postSuccessContent}</div>
      ) : null}
    </article>
  );
}
